import { VehicleStatus } from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { VehicleRelatedDto } from "@1games-ru/region-shared/dist/types/vehicle/vehicle-b";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import VehicleCdn from "@/core/cdn/vehicle/vehicle-cdn.ts";
import { KeyIcon, Vehicle2Icon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import { ModalProps } from "@/shared/ui/modal/Modal";

type Props = ModalProps & {
  onAdd: (vehicle: VehicleRelatedDto) => void;
  vehicles: VehicleRelatedDto[];
  isLoading: boolean;
  isUpdating: boolean;
};

export default function EstateVehiclesAddModal({ onAdd, vehicles, isLoading, ...props }: Props) {
  const { t } = useTranslation("estate");
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleRelatedDto | null>(null);

  const selectVehicle = useCallback((newVehicle: VehicleRelatedDto) => {
    setSelectedVehicle((vehicle) => {
      if (!vehicle || (vehicle && vehicle.vehicleId !== newVehicle.vehicleId)) {
        return newVehicle;
      }
      return null;
    });
  }, []);

  const footer = (
    <Button className="relative h-11 mr-[0.8125rem]" onClick={() => onAdd(selectedVehicle)}>
      {t("estate.vehicles.add.addInGarage")}
    </Button>
  );
  const title = (
    <div className="flex flex-col gap-1">
      <div className="text-18 font-bold text-white">{t("estate.vehicles.add.title")}</div>
      <div className="text-15 font-medium text-white/60">{t("estate.vehicles.add.desc")}</div>
    </div>
  );

  return (
    <Modal
      {...props}
      title={title}
      classNameOverlay="absolute inset-3 rounded-[1rem]"
      className="w-[39.75rem] h-[36.75rem] pr-[0.8125rem]"
      container="tablet"
      footer={footer}
      classNameChildren="[--scrollbar-my:0] [--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242]"
    >
      <div className="relative grid grid-cols-3 gap-1.5 pr-[0.8125rem]">
        {vehicles.map((item, index) => {
          let hintPosition: "left" | "bottom" | "right" = "right";
          if ((index + 1) % 2 === 0) hintPosition = "bottom";
          if ((index + 1) % 3 === 0) hintPosition = "left";

          return (
            <button
              type="button"
              key={item.vehicleId}
              className={clsx(
                "group relative",
                "h-[10.3125rem] flex flex-col items-center gap-2.5 p-2.5 rounded-[0.75rem]",
                "group/card",
                selectedVehicle && item.vehicleId === selectedVehicle.vehicleId
                  ? "bg-gradient-to-br from-[#d73434]/50 to-transparent"
                  : "bg-dark-800",
              )}
              onClick={() => selectVehicle(item)}
            >
              <div className="absolute top-0 left-0 size-full bg-gradient-to-br from-[#FF4E4E]/30 to-transparent rounded-2xl transition-all duration-200 ease-in-out opacity-0 group-hover:opacity-100 group-active:brightness-90" />
              <div className="relative">
                {item.address ? (
                  <div className="absolute left-0 top-0 z-3">
                    <div className="relative group/hint flex items-center justify-center w-[1.5rem] h-[1.5rem] rounded-[0.5rem] bg-gray/50">
                      <Vehicle2Icon size={16} className="text-inverted" />
                      <div
                        className={clsx(
                          "absolute group-hover/hint:flex hidden text-14 mt-1 top-[100%] font-medium text-gray/50 card rounded-[0.5rem] px-2.5 text-nowrap py-1 z-4",
                          {
                            "right-0  mr-1": hintPosition === "left",
                            "left-[100%] -translate-x-[50%]": hintPosition === "bottom",
                            "left-0 top-[100%]": hintPosition === "right",
                          },
                        )}
                      >
                        В гараже:{" "}
                        <span className="text-inverted ml-1">
                          {item.address.streetName} {item.address.index}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <>
                    {item.status !== VehicleStatus.Pending ? (
                      <div
                        className={clsx(
                          "flex items-center justify-center z-1",
                          "absolute left-0 top-0 w-[1.5rem] h-[1.5rem] rounded-[0.5rem] bg-gray/50",
                        )}
                      >
                        <KeyIcon size={16} className="text-inverted" />
                      </div>
                    ) : null}
                  </>
                )}
                {item.status !== VehicleStatus.Pending && (
                  <div
                    className={clsx(
                      "absolute left-0 bottom-0 text-10 font-bold text-gray z-1",
                      "bg-gradient-to-r from-gray/50 via-50% via-gray/50 to-accent-muted/30 py-2 px-2.5 rounded-[0.375rem] backdrop-blur-[2px]",
                    )}
                  >
                    {t(`estate.vehicles.status.${item.status.toLowerCase()}`)}
                  </div>
                )}
                <img
                  src={VehicleCdn.getVehicleImage(item.model)}
                  className={clsx(
                    "h-[6rem] w-[10.75rem]",
                    item.status !== VehicleStatus.Pending ? "brightness-[30%]" : "",
                  )}
                  alt={item.name.modelName}
                />
              </div>
              <div className="flex flex-col h-full w-full overflow-clip">
                <div className="text-white text-15 font-medium text-left">
                  {item.name.brandName}
                </div>
                <div className="text-white/50 text-15 font-bold text-left mt-0.5 group-hover/card:text-accent-solid transition-all">
                  {item.name.modelName}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
